import { activity_recent, and, desc, eq, type PowerSyncDrizzleDB } from "@comandero/powersync";

const DEFAULT_FEED_LIMIT = 200;

/**
 * PLUGIN-KERNEL: the app-specific dimensions the feed can be narrowed by.
 *
 * comandero's feed is filterable by shift and by branch. `feed-source.ts` and
 * the activity plugin are generic over this type and never look inside it, so
 * this interface is the ONLY place the POS dimensions appear.
 */
export interface ActivityFeedFilter {
  /** "Shift activity" view: filters by cashbox session.*/
  registerSessionId?: string;
  /** Filters by active branch.*/
  branchId?: string;
}

export interface ActivityFeedOptions extends ActivityFeedFilter {
  limit?: number;
}

/**
 * Local activity feed for the household (PowerSync `activity_recent` bucket, ~90
 * The account's SQLite only contains buckets of its organizations,
 * so there is no need to re-filter by user/org.
 * Canonical order: server_ts DESC (UUIDv7 id as tiebreaker).
 */
export function getActivityFeedQuery(db: PowerSyncDrizzleDB, options: ActivityFeedOptions = {}) {
  const filters = [];
  if (options.registerSessionId) {
    filters.push(eq(activity_recent.register_session_id, options.registerSessionId));
  }
  if (options.branchId) {
    filters.push(eq(activity_recent.branch_id, options.branchId));
  }
  const base = db.select().from(activity_recent);
  const withWhere = filters.length > 0 ? base.where(and(...filters)) : base;
  return withWhere
    .orderBy(desc(activity_recent.server_ts), desc(activity_recent.id))
    .limit(options.limit ?? DEFAULT_FEED_LIMIT);
}

/**
 * Local history for a specific entity (for the details "History" sheet). The
 * deep history (beyond retention) is requested from the API
 * (`GET /api/activity/entity/:type/:id`).
 */
export function getEntityActivityQuery(
  db: PowerSyncDrizzleDB,
  entityType: string,
  entityId: string,
  options: { limit?: number } = {},
) {
  return db
    .select()
    .from(activity_recent)
    .where(
      and(eq(activity_recent.entity_type, entityType), eq(activity_recent.entity_id, entityId)),
    )
    .orderBy(desc(activity_recent.server_ts), desc(activity_recent.id))
    .limit(options.limit ?? DEFAULT_FEED_LIMIT);
}
